<template>
  <teleport to="body">
    <div class="modal" v-if="showModal" @click.self.prevent="closed">
      <div class="wrapper">
        <font-awesome-icon icon="fa-xmark" class="exit" @click="closed" />
        <section class="content">
          <h3>Float Control</h3>
          <h4 v-if="stationName">{{ stationName }}</h4>
          <div class="info">
            <div class="till-value">
              <p>Cash in till</p>
              <span class="amount">
                <CurrencyFormatter :value="floatAmount" :currency="currency" />
              </span>
            </div>

            <div class="history-chart">
              <div class="chart-header">
                <p class="section-label">Float · {{ weekLabel }}</p>
                <div class="week-nav">
                  <button :disabled="!canGoOlder" @click="weekOffset++">&#8592;</button>
                  <button :disabled="!canGoNewer" @click="weekOffset--">&#8594;</button>
                </div>
              </div>
              <VChart v-if="chartData.length > 1" :option="chartOptions" class="chart" autoresize />
              <p v-else class="empty">No activity for this week</p>
            </div>

            <div class="removals">
              <p class="section-label">Recent cash removals</p>
              <ul v-if="pagedRemovals.length">
                <li v-for="(removal, idx) in pagedRemovals" :key="idx">
                  <span class="removal-amount">
                    <CurrencyFormatter :value="removal.value" :currency="currency" />
                  </span>
                  <span class="removal-reason">{{ removal.reason || "—" }}</span>
                  <span class="removal-meta">{{ formatDate(removal.timestamp) }}<template v-if="removal.name"> · {{ removal.name }}</template></span>
                </li>
              </ul>
              <p v-else class="empty">No recent cash removals</p>
              <div v-if="totalPages > 1" class="pagination">
                <button :disabled="page === 0" @click="page--">&#8592;</button>
                <span>{{ page + 1 }} / {{ totalPages }}</span>
                <button :disabled="page >= totalPages - 1" @click="page++">&#8594;</button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </teleport>
</template>

<script>
import { mapGetters } from "vuex";
import { CurrencyFormatter } from "@monolith/components";
import * as echarts from "echarts/core";
import { GridComponent, TooltipComponent } from "echarts/components";
import { LineChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import VChart from "vue-echarts";

echarts.use([CanvasRenderer, LineChart, GridComponent, TooltipComponent]);

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

export default {
  name: "FloatModal",
  props: ["open", "floatAmount", "stationName", "removals", "history"],
  emits: ["close"],
  components: { CurrencyFormatter, VChart },
  data() {
    return {
      showModal: false,
      page: 0,
      weekOffset: 0,
    };
  },
  computed: {
    ...mapGetters({
      companySettings: "companySettings",
    }),
    currency() {
      return this.companySettings.currency || "NZD";
    },
    pagedRemovals() {
      const start = this.page * 7;
      return (this.removals || []).slice(start, start + 7);
    },
    totalPages() {
      return Math.ceil((this.removals || []).length / 7);
    },
    weekWindow() {
      const end = Date.now() - this.weekOffset * WEEK_MS;
      return { start: end - WEEK_MS, end };
    },
    weekLabel() {
      const fmt = (ts) => new Date(ts).toLocaleDateString([], { month: "short", day: "numeric" });
      return this.weekOffset === 0 ? `Last 7 days` : `${fmt(this.weekWindow.start)} – ${fmt(this.weekWindow.end)}`;
    },
    canGoNewer() {
      return this.weekOffset > 0;
    },
    canGoOlder() {
      const entries = this.history || [];
      if (!entries.length) return false;
      const oldest = entries[entries.length - 1];
      return new Date(oldest.timestamp).getTime() < this.weekWindow.start;
    },
    chartData() {
      // history is sorted most-recent-first; walk backwards from the
      // current till value to reconstruct the balance after each entry
      const entries = this.history || [];
      let balance = this.floatAmount || 0;
      const points = [];
      const { start, end } = this.weekWindow;

      entries.forEach((entry, idx) => {
        if (idx > 0) {
          const previous = entries[idx - 1];
          const delta = previous.doc_type === "debit" ? -previous.value : previous.value;
          balance -= delta;
        }
        const ts = new Date(entry.timestamp).getTime();
        if (ts >= start && ts <= end) {
          points.push([entry.timestamp, balance]);
        }
      });

      points.reverse();
      if (this.weekOffset === 0) {
        points.push([new Date().toISOString(), this.floatAmount || 0]);
      }
      return points;
    },
    chartOptions() {
      const actionColour = getComputedStyle(document.body).getPropertyValue("--action-colour").trim() || "#39AAD4";
      return {
        grid: { left: 55, right: 20, top: 20, bottom: 30 },
        xAxis: {
          type: "time",
          axisLabel: { formatter: (value) => new Date(value).toLocaleDateString([], { month: "short", day: "numeric" }) },
        },
        yAxis: {
          type: "value",
          axisLabel: { formatter: (value) => new Intl.NumberFormat([], { style: "currency", currency: this.currency, maximumFractionDigits: 0 }).format(value) },
        },
        tooltip: {
          trigger: "axis",
          valueFormatter: (value) => new Intl.NumberFormat([], { style: "currency", currency: this.currency }).format(value),
        },
        series: [
          {
            data: this.chartData,
            type: "line",
            step: "end",
            symbolSize: 6,
            itemStyle: { color: actionColour },
            lineStyle: { color: actionColour },
            areaStyle: { color: actionColour + "30" },
          },
        ],
      };
    },
  },
  watch: {
    open() {
      this.showModal = this.open;
      this.page = 0;
      this.weekOffset = 0;
    },
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return "—";
      return new Date(timestamp).toLocaleString([], { dateStyle: "short", timeStyle: "short" });
    },
    closed() {
      this.$emit("close");
      this.showModal = false;
    },
  },
};
</script>

<style scoped lang="scss">
.wrapper {
  min-width: 420px;
  max-width: 90vw;
}

.till-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem;

  p {
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 0.75rem;
    opacity: 0.6;
  }

  .amount {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--action-colour);
  }
}

.history-chart {
  padding: 0 1.5rem 1rem;

  .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .section-label {
    margin: 0 0 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 0.7rem;
    opacity: 0.6;
  }

  .week-nav {
    display: flex;
    gap: 0.3rem;
    margin-bottom: 0.5rem;

    button {
      padding: 0.15rem 0.5rem;
      border: 1px solid var(--border-colour);
      border-radius: 0.3rem;
      background: var(--secondary-colour);
      color: var(--text-colour);
      cursor: pointer;
      &:disabled {
        opacity: 0.35;
        cursor: default;
      }
    }
  }

  .chart {
    height: 180px;
    width: 100%;
  }

  .empty {
    margin: 0;
    font-style: italic;
    opacity: 0.5;
    font-size: 0.85rem;
  }
}

.removals {
  padding: 0 1.5rem 1.5rem;

  .section-label {
    margin: 0 0 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 0.7rem;
    opacity: 0.6;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  li {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    column-gap: 0.75rem;
    align-items: baseline;
    padding: 0.5rem 0.7rem;
    border-radius: 0.4rem;
    background-color: var(--tertiary-colour);
  }

  .removal-amount {
    grid-row: 1 / span 2;
    align-self: center;
    font-weight: 700;
    color: var(--action-colour);
  }

  .removal-reason {
    font-size: 0.9rem;
  }

  .removal-meta {
    font-size: 0.72rem;
    opacity: 0.55;
  }

  .empty {
    margin: 0;
    font-style: italic;
    opacity: 0.5;
    font-size: 0.85rem;
  }

  .pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-top: 0.75rem;

    button {
      padding: 0.25rem 0.6rem;
      border: 1px solid var(--border-colour);
      border-radius: 0.3rem;
      background: var(--secondary-colour);
      color: var(--text-colour);
      cursor: pointer;
      &:disabled {
        opacity: 0.35;
        cursor: default;
      }
    }

    span {
      font-size: 0.8rem;
      opacity: 0.65;
    }
  }
}
</style>
